/**
 * THIS IS A VERSIONED FILE - see readme for more info
 */

/** NOTE: IF A FUNCTION CALLS ANOTHER FUNCTION IN THIS SAME FILE THEN YOU MUST NOT IMPORT THAT FUNCTION INTO LATER VERSIONS, EVEN IF THERE ARE NO CHANGES */

import {
  CARD_NORMAL,
  CARD_AGREEMENT,
  CARD_STATEMENT,
  DATA_SECTION,
  STATUS_APPROVED,
  DATA_GROUP,
  DATA_PAGE,
  PAGE_PROPERTY_MONEY_AND_PROPERTY,
  PARTY_A,
  PARTY_B,
  Sections,
} from 'Common/constants';
import MatterPropsJS, { getMatter } from 'Common/Utils/MatterProps';

import { ODRS_DASH_HOME_ROUTE } from 'Common/routes';
import generateRelationship from './Sections/relationship';
import generateProperty from './Sections/property';
import generateParenting from './Sections/parenting';
import generateConsentOrders from './Sections/consentOrders';

import generateParentingPlanSectionData from '../Questions/ParentingPlan';
import {
  CardAny,
  DataGroup,
  DataPage,
  DataSection,
} from '../../../Types/appSections';
import { Item } from '../../../Types/matter';

// return an array of existing children cards, property cards, and vehicle cards respectively
const getExistingRepeatableCards = (items: Item[]) => [
  items.filter(item => item.BaseSection === 'children'),
  items.filter(item => item.BaseSection === 'properties'),
  items.filter(item => item.BaseSection === 'vehicles'),
];

export const getAppSections = (): DataSection[] => {
  const { items } = getMatter();

  let parentingPlanBasics;
  let parentingPlanScheduling;
  let parentingPlanAdditions;

/////////////////////////////
///////////////////////////
///////////////////////////////
//////////////////////////////
///////////
  // Need to pass children data to generate questions
  ({
    basics: parentingPlanBasics,
    scheduling: parentingPlanScheduling,
    additions: parentingPlanAdditions,
  } = generateParentingPlanSectionData(MatterPropsJS('childrenData')));
////////////

  const [existingChildren, existingProperties, existingVehicles] =
    getExistingRepeatableCards(items);

  const existingAgreedChildren: Item[] = existingChildren.filter(
    (item: Item) => item.status === STATUS_APPROVED && item.SectionID
  );
  const existingAgreedProperties: Item[] = existingProperties.filter(
    (item: Item) => item.status === STATUS_APPROVED && item.SectionID
  );

  const propertySection: any = generateProperty({
    existingProperties,
    existingVehicles,
  });

  // @TODO refactor this to not have this stuff hardcoded
  const superGroup = propertySection?.children
    .find(
      (p: DataPage) =>
        p.type === DATA_PAGE && p.id === PAGE_PROPERTY_MONEY_AND_PROPERTY
    )
    .children.find(
      (g: DataGroup) => g.type === DATA_GROUP && g.id === 'superannuation'
    );
  const superA = superGroup?.children.find(
    (c: Item) => c.type === CARD_NORMAL && c.cardID === 'yoursuperannuationA'
  );
  const superB = superGroup?.children.find(
    (c: Item) => c.type === CARD_NORMAL && c.cardID === 'yoursuperannuationB'
  );

  const doesSuperExist = {
    [PARTY_A]: superA.data && superA.data.superannuationYesNo,
    [PARTY_B]: superB.data && superB.data.superannuationYesNo,
  };

  return [
    propertySection,
    generateRelationship({ existingChildren }),
    generateParenting({
      parentingPlanBasics,
      parentingPlanScheduling,
      parentingPlanAdditions,
    }),
    generateConsentOrders({
      existingAgreedChildren,
      existingAgreedProperties,
      doesSuperExist,
    }),
  ];
};

const getIDAsArray = (id: string) =>
  Array.isArray(id) ? `${id[0]}${id[1] || ''}` : id;

const sectionsFlattened = (
  sections: DataSection[]
): (DataSection | DataGroup | DataPage | CardAny)[] => {
  const reducer = (array: any, el: any) => {
    const { children } = el;
    delete el.children;
    array.push(el);

    if (children) {
      return children.reduce(reducer, array);
    }

    return array;
  };

  return sections.reduce(reducer, []);
};

export const getSection = (sectionID: Sections): DataSection | undefined =>
  getAppSections().find((section: DataSection) => section.id === sectionID);

export const getPage = (
  sectionID: Sections,
  pageID: string
): DataPage | undefined => {
  try {
    return getSection(sectionID)?.children.find(
      (page: DataPage) => page.id === pageID
    );
  } catch (error) {
    throw Error(`Page does not exist (${sectionID} / ${pageID})`);
  }
};

export const getOptionalCardIDsInSection = (sectionID: Sections) => {
  const section = getSection(sectionID);

  if (section) {
    return sectionsFlattened([])
      .filter(
        (item: any) =>
          item.type &&
          [CARD_AGREEMENT, CARD_STATEMENT, CARD_NORMAL].includes(item.type) &&
          item.optional
      )
      .map((item: any) => item.cardID || undefined);
  }

  return [];
};

export const getCard = (id: string): CardAny => {
  const cardID = getIDAsArray(id);

  const sections = getAppSections();
  const sectionsFlat = sectionsFlattened(sections);

  const card = sectionsFlat.find((el: any) => el.cardID === cardID) as CardAny;

  if (!card) {
    throw new Error(`Card with ID ${id} not found`);
  }

  return card;
};

/** Gets a card and returns it as the provided card type
 *
 * @throws if the card is undefined
 *
 * @param cardID - card id
 * @returns returns the card as the provided type
 *
 * @example
 *
 * // card is now definitely defined and of type CardYourSuperannuation
 * const card = definitelyGetCard<CardYourSuperannuation>('yoursuperannuation1A');
 */
export const definitelyGetCard = <T extends CardAny>(cardID: string) => {
  const cardMaybe = getCard(cardID);

  if (cardMaybe === undefined) {
    throw Error(`Card is undefined (${cardID})`);
  }

  return cardMaybe as T;
};

export const getGroupContainingCard = (id: string) => {
  const cardID = getIDAsArray(id);

  const sections = getAppSections();
  const sectionsFlat = sectionsFlattened(sections);

  const index = sectionsFlat.findIndex((el: any) => el.cardID === cardID);

  const partialReversed = sectionsFlat.slice(0, index).reverse();

  const group = partialReversed.find((el: any) => el.type === DATA_GROUP);

  if (!group) {
    throw new Error(`Card '${id}' does not have a group`);
  }

  // typecasting this to keep Typescript happy in other places
  // we are confirming that it is definitely of this type a few lines up
  return group as DataGroup;
};

export const getCardDashboardRoute = (id: string) => {
  const cardID = getIDAsArray(id);

  const sections = getAppSections();
  const sectionsFlat = sectionsFlattened(sections);

  const index = sectionsFlat.findIndex((item: any) => item.cardID === cardID);

  const partialReversed = sectionsFlat.slice(0, index).reverse();

  const page = partialReversed.find(
    (el: any) => el.type === DATA_PAGE
  ) as DataPage;

  return page ? page.route : ODRS_DASH_HOME_ROUTE;
};

export const getCardQuestionFlowMetadata = (id: string) => {
  const cardID = getIDAsArray(id);

  const sections = getAppSections();
  const sectionsFlat = sectionsFlattened(sections);

  const index = sectionsFlat.findIndex((item: any) => item.cardID === cardID);

  const partialReversed = sectionsFlat.slice(0, index).reverse();

  const group = (partialReversed.find((el: any) => el.type === DATA_GROUP) ||
    {}) as DataGroup;

  const section = (partialReversed.find(
    (el: any) => el.type === DATA_SECTION
  ) || {}) as DataSection;

  return {
    backgroundImage: section?.questionFlowBackgroundImage,
    backgroundColour: section?.questionFlowBackgroundColour,
    icon: group?.icon || undefined,
  };
};

export const isConsentOrdersDashboardLocation = (location: any) => {
  const consentOrderSlugs = [
    // Main path
    '/consent-orders/',
  ];
  return (
    location &&
    consentOrderSlugs.some(param => location.pathname.includes(param))
  );
};

export const isConsentOrdersQuestionLocation = (location: any) => {
  const consentOrderSlugs = [
    // Question path
    'about-continue',
    'bank-account',
    'children-continue',
    'jurisdiction',
    'properties-continue',
    'requirements',
    'your-contributions',
    'yourincome',
    'yoursuperannuationcontinue',
    'relationship-continue',
    'your-superannuation-continue',
    'return-specific-items',
    'asset-table',
    'properties-continue',
    'your-contributions',
    'your-income',
    'bank-account',
    'acquired-and-sold-items',
  ];
  return (
    location &&
    consentOrderSlugs.some(param => location.pathname.includes(param))
  );
};

/**
 * Gets the page and section for a card ID
 * @param id - card id
 * @returns - returns the section and page for the card
 */
export const getPageAndSectionIdsContainingCard = (id: string) => {
  const cardID = getIDAsArray(id);
  const sections = getAppSections();
  const sectionsFlat = sectionsFlattened(sections);
  const index = sectionsFlat.findIndex((el: any) => el.cardID === cardID);
  const partialReversed = sectionsFlat.slice(0, index).reverse();
  const section: any = partialReversed.find(
    (el: any) => el.type === DATA_SECTION
  );
  const page: any = partialReversed.find((el: any) => el.type === DATA_PAGE);
  if (!section || !page) return {};
  return {
    section,
    page,
  };
};
